import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BILLING_METHOD_LABELS, BILLING_PAYMENT_STATUS_LABELS, berlinDate, formatCents, type BillingPaymentStatus, type OperatorBillingPayment } from '@kontor/shared';
import { ApiError } from '../api';
import { operatorApi } from './operatorApi';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const day = (iso: string) => dateFormat.format(new Date(`${iso}T12:00:00`));
const tone = (s: BillingPaymentStatus) => (s === 'paid' ? 'badge--won' : s === 'open' ? 'badge--in_progress' : s === 'cancelled' || s === 'refunded' ? '' : 'badge--rejected');

/** Zahlungen der Kunden (KON-181): offene und überfällige zuerst, eingegangene Überweisungen werden hier eingetragen. Sie landen auf der Rechnung im Mandanten Wirkia. */
export function OperatorBillingPage() {
  const client = useQueryClient();
  const [all, setAll] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [paidOn, setPaidOn] = useState(berlinDate());
  const [reference, setReference] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const list = useQuery({ queryKey: ['operator-billing', all], queryFn: async () => (await operatorApi<{ data: OperatorBillingPayment[] }>('GET', `/billing/payments?status=${all ? 'all' : 'open'}`)).data });
  const pay = useMutation({
    mutationFn: (id: string) => operatorApi('POST', `/billing/payments/${id}/paid`, { paidOn, reference: reference || null }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['operator-billing'] });
      setEditing(null);
      setReference('');
      setMessage({ ok: true, text: 'Der Zahlungseingang ist eingetragen.' });
    },
    onError: (e) => setMessage({ ok: false, text: e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : 'Das Eintragen hat nicht geklappt.' }),
  });
  const invoice = useMutation({
    mutationFn: (id: string) => operatorApi<{ data: OperatorBillingPayment }>('POST', `/billing/payments/${id}/invoice`),
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['operator-billing'] });
      setMessage({ ok: true, text: `Die Rechnung ${res.data.invoiceNumber} ist ausgestellt und dem Kunden per E-Mail geschickt.` });
    },
    onError: (e) => setMessage({ ok: false, text: e instanceof ApiError ? e.message : 'Die Rechnung ließ sich nicht ausstellen.' }),
  });
  const run = useMutation({
    mutationFn: () => operatorApi<{ data: { invoiced: number; reminded: number; suspended: number; ended: number } }>('POST', '/billing/run'),
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['operator-billing'] });
      const r = res.data;
      setMessage({ ok: true, text: `Lauf beendet: ${r.invoiced} neue Rechnungen, ${r.reminded} Erinnerungen, ${r.suspended} Sperren, ${r.ended} beendete Bestellungen.` });
    },
    onError: () => setMessage({ ok: false, text: 'Der Lauf hat nicht geklappt.' }),
  });

  return (
    <>
      <p><Link to="/betreiber" className="back-link">← Alle Mandanten</Link></p>
      <div className="page-head">
        <h1>Zahlungen</h1>
        <label className="check page-head__action">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Auch bezahlte zeigen
        </label>
      </div>
      <p className="muted">Offene und überfällige Zahlungen der Kunden. Erinnerung sieben Tage und Sperre 21 Tage nach der Fälligkeit erledigt der stündliche Lauf.</p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      <div className="table-wrap">
        <table className="table table--cards">
          <thead>
            <tr><th>Kunde</th><th>Zeitraum</th><th>Betrag</th><th>Status</th><th>Fällig</th><th>Rechnung</th><th><span className="sr-only">Aktion</span></th></tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={7} className="muted">Wird geladen …</td></tr>}
            {list.data && list.data.length === 0 && <tr><td colSpan={7} className="empty">Keine offenen Zahlungen.</td></tr>}
            {list.data?.map((p) => (
              <tr key={p.id}>
                <td><Link to={`/betreiber/mandanten/${p.tenantId}`} className="strong-link">{p.tenantName}</Link><div className="muted small">Nr. {p.tenantNumber} · {BILLING_METHOD_LABELS[p.paymentMethod]}</div></td>
                <td data-label="Zeitraum">{day(p.periodStart)} bis {day(p.periodEnd)}</td>
                <td data-label="Betrag">{formatCents(p.grossCents)}</td>
                <td data-label="Status"><span className={`badge ${tone(p.status)}`}>{BILLING_PAYMENT_STATUS_LABELS[p.status]}</span></td>
                <td data-label="Fällig">{p.dueOn ? day(p.dueOn) : '–'}</td>
                <td data-label="Rechnung">{p.invoiceNumber ?? <span className="muted">fehlt</span>}</td>
                <td data-label="Aktion">
                  {p.status === 'paid' && !p.hasInvoice && <button className="btn btn--secondary btn--sm" type="button" disabled={invoice.isPending} onClick={() => { setMessage(null); invoice.mutate(p.id); }}>Rechnung ausstellen</button>}
                  {p.status !== 'paid' && p.status !== 'cancelled' && p.status !== 'refunded' && editing !== p.id && <button className="btn btn--secondary btn--sm" type="button" onClick={() => { setEditing(p.id); setMessage(null); }}>Zahlung eingegangen</button>}
                  {editing === p.id && (
                    <div className="billing-pay">
                      <label className="field"><span>Eingegangen am</span><input className="input" type="date" value={paidOn} max={berlinDate()} onChange={(e) => setPaidOn(e.target.value)} /></label>
                      <label className="field"><span>Verwendungszweck (freiwillig)</span><input className="input" value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} /></label>
                      <div className="form-actions">
                        <button className="btn btn--primary btn--sm" type="button" disabled={pay.isPending} onClick={() => pay.mutate(p.id)}>Eintragen</button>
                        <button className="btn btn--secondary btn--sm" type="button" onClick={() => setEditing(null)}>Abbrechen</button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="form-actions">
        <button className="btn btn--secondary" type="button" disabled={run.isPending} onClick={() => run.mutate()}>{run.isPending ? 'Läuft …' : 'Lauf jetzt starten'}</button>
      </div>
    </>
  );
}
